import { groupBy } from 'lodash'
import { useMemo } from 'react'
import { useNavigate, useMatch } from 'react-router-dom'
import {
  DelimitedArrayParam,
  QueryParamConfig,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params'

import { useMarketplace } from '../useMarketplace'

const queryParamsConfig: Record<string, QueryParamConfig<any, any>> = {
  categories: withDefault(DelimitedArrayParam, []),
  deliveryZone: StringParam,
}

export const useCategories = () => {
  const marketplace = useMarketplace()
  const navigate = useNavigate()
  const [categoryNames, setCategoryNames] = useQueryParams(queryParamsConfig)
  const isOutletListRoute = useMatch(
    `/:merchantCategory(${marketplace.urlPath})`
  )

  const setCategoryNamesAndRedirectIfNotOutletList = ({
    categories,
  }: {
    categories: string[] | undefined
  }) => {
    // Back button on category filtered outlets
    if (!categories) return setCategoryNames({ categories: undefined })

    // Keeps user on category filtered outlets even if none selected until explicitly exited
    setCategoryNames({ categories: categories.length ? categories : [] })

    if (isOutletListRoute) {
      navigate(`${marketplace.urlPath}${location.search}`, {
        replace: true,
      })
    }
  }

  const topCategories = marketplace.categories.filter(
    ({ id }) => id === 'deals' || id === 'featured'
  )

  const allCategories = marketplace.categories.filter(
    ({ id }) => id !== 'deals' && id !== 'featured'
  )

  const categoriesByFirstLetter = groupBy(allCategories, category =>
    category.name.slice(0, 1)
  )

  const selectedCategories = useMemo(() => {
    return marketplace.categories.filter(({ name }) =>
      categoryNames.categories.includes(name)
    )
  }, [categoryNames, marketplace])

  const toggleCategory = (categoryName: string) => {
    const isSelected = categoryNames.categories.includes(categoryName)
    const newCategories = isSelected
      ? categoryNames.categories.filter((name: string) => name !== categoryName)
      : [...categoryNames.categories, categoryName]

    setCategoryNamesAndRedirectIfNotOutletList({ categories: newCategories })
  }

  return {
    topCategories,
    categoriesByFirstLetter,
    selectedCategories,
    setCategoryNames: setCategoryNamesAndRedirectIfNotOutletList,
    toggleCategory,
  }
}
